import { useState, useEffect, useRef } from 'react';
import { Truck, Lightbulb, Sparkles, Settings as SettingsIcon, Battery, Activity, MapPinned, Wrench, Users, Database, Code2, Bot, Leaf, Calendar, ChartNoAxesCombined, PoundSterling, HeartPulse, SlidersHorizontal, Package, FileText, Gauge, Layers, Eye, Upload, Crosshair, TrendingUp, Cpu, MessagesSquare, CalendarClock, type LucideIcon } from 'lucide-react';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import FleetReport from './tabs/FleetReport';
import Incidents from './tabs/Incidents';
import Optimization from './tabs/Optimization';
import Electrification from './tabs/Electrification';
import FuturePlanning from './tabs/FuturePlanning';
import Settings from './tabs/Settings';
import DataFeed from './tabs/DataFeed';
import FleetDataHub from './tabs/FleetDataHub';
import AIDispatch from './tabs/AIDispatch';
import ApiSources from './tabs/ApiSources';
import ApiPlayground from './tabs/ApiPlayground';
import CustomsAgent from './tabs/CustomsAgent';
import Configuration from './tabs/Configuration';
import Assets from './tabs/Assets';
import Collaborate from './tabs/Collaborate';
import Backloader from './tabs/Backloader';
import CarbonReporting from './tabs/CarbonReporting';
import ClientReporting from './tabs/ClientReporting';
import CostManage from './tabs/CostManage';
import Maintenance from './tabs/Maintenance';
import ProfitabilityReport from './tabs/ProfitabilityReport';
import SystemHealth from './tabs/SystemHealth';
import ProfitAgent from './tabs/ProfitAgent';
import OpsProfit from './tabs/OpsProfit';
import FleetIntelligence from './tabs/FleetIntelligence';
import VehicleSceneTimeline from './tabs/VehicleSceneTimeline';
import MaintenanceTruth from './tabs/MaintenanceTruth';
import TruthCompute from './tabs/TruthCompute';
import ZoeQuestionLog from './tabs/ZoeQuestionLog';
import ZoeBriefs from './tabs/ZoeBriefs';
import { OPEN_PAGES, filterTabsByAccess } from './utils/tabAccess';
import Investigator from './tabs/Investigator';
import PricingIntelligencePage from './tabs/PricingIntelligencePage';
import { getUnsavedChangesWarning } from './utils/unsavedChangesGuard';
import DataImport from './components/fleetIntelligence/DataImport';
import Login from './components/Login';
import Agents from './tabs/Agents';
import ZoeMark from './components/zoeAssistant/ZoeMark';
import ZoeLauncher from './components/zoeAssistant/ZoeLauncher';
import { ZoeConversationProvider } from './contexts/ZoeConversationContext';
import { useUseCase } from './contexts/UseCaseContext';
import { TabProvider } from './contexts/TabContext';
import { onSessionExpired, emitSessionExpired } from './auth/sessionEvents';

// How often to proactively renew the Cognito session while the app is open.
// Well under the ~60min access/ID token lifetime so refreshSession()'s own
// "expiring within 60s" check always has room to renew before expiry.
const SESSION_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

import { PlanningDecisionPage } from './components/planning/PlannerDecisionPanel';

const TAB_HASH_PREFIX = '#tab=';

interface AppTab {
  id: string;
  label: string;
  icon: LucideIcon;
  clickable: boolean;
  indent?: boolean;
  useCase?: 'logistics' | 'emergency' | 'both';
  children?: AppTab[];
}

function getTabFromLocationHash(): string | null {
  try {
    const h = window.location.hash || '';
    if (!h.startsWith(TAB_HASH_PREFIX)) return null;
    const tabId = decodeURIComponent(h.slice(TAB_HASH_PREFIX.length)).trim();
    return tabId || null;
  } catch {
    return null;
  }
}

function setTabLocationHash(tabId: string, mode: 'push' | 'replace') {
  try {
    const nextHash = `${TAB_HASH_PREFIX}${encodeURIComponent(tabId)}`;
    if (window.location.hash === nextHash) return;
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    if (mode === 'replace') {
      window.history.replaceState({ tabId }, '', nextUrl);
    } else {
      window.history.pushState({ tabId }, '', nextUrl);
    }
  } catch {
    // ignore history errors
  }
}

/** Strip #tab=... so marketing / login URLs stay clean (no internal tab in address bar). */
function clearTabLocationHash() {
  try {
    if (!window.location.hash.startsWith(TAB_HASH_PREFIX)) return;
    const url = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, '', url);
  } catch {
    // ignore
  }
}

// Shared tabs (available for both use cases)
const sharedTabs: AppTab[] = [];

// Logistics-specific tabs
const logisticsTabs: AppTab[] = [
  {
    // ===== ZOE =====
    id: 'agents',
    label: 'ZOE',
    icon: ZoeMark,
    clickable: true,
    useCase: 'logistics',
    children: [
      { id: 'customs-agent', label: 'Document Agent', icon: FileText, clickable: true, useCase: 'logistics' },
      { id: 'profit-agent', label: 'Profit Agent', icon: PoundSterling, clickable: true, useCase: 'logistics' },
      // Open to everyone in the organisation (OPEN_PAGES); changing briefs needs `zoe-briefs`, enforced by zeecore.
      { id: 'zoe-briefs', label: 'Briefs', icon: CalendarClock, clickable: true, useCase: 'logistics' },
    ],
  },

  {
    // ===== CONTROL CENTRE =====
    id: 'fleet-data-hub',
    label: 'Control Centre',
    icon: Database,
    clickable: true,
    useCase: 'logistics',
    children: [
      { id: 'api-sources', label: 'API Sources', icon: Code2, clickable: true, useCase: 'logistics' },
      { id: 'api-playground', label: 'API Playground', icon: Bot, clickable: true, useCase: 'logistics' },
      { id: 'data-feed', label: 'Data Upload', icon: Upload, clickable: true, useCase: 'logistics' },
    ],
  },

  {
    // ===== PROFIT (historical analysis — before Assets) =====
    id: 'ops-profit',
    label: 'Profit',
    icon: PoundSterling,
    clickable: true,
    useCase: 'logistics',
  },

  {
    // ===== SYSTEM HEALTH (data freshness + 7-day health trends; moved out of Control Centre) =====
    id: 'system-health',
    label: 'System Health',
    icon: HeartPulse,
    clickable: true,
    useCase: 'logistics',
  },

  {
    // ===== ASSETS =====
    id: 'assets',
    label: 'Assets',
    icon: Truck,
    clickable: true,
    useCase: 'logistics',
  },

  {
    // ===== CONFIGURATION =====
    id: 'configuration',
    label: 'Configuration',
    icon: SlidersHorizontal,
    clickable: true,
    useCase: 'logistics',
  },

  {
    // ===== FLEET REPORT =====
    id: 'fleet-group',
    label: 'Fleet Report',
    icon: ChartNoAxesCombined,
    clickable: true,
    useCase: 'logistics',
    children: [
      { id: 'profitability-report', label: 'Profit (legacy)', icon: PoundSterling, clickable: true, useCase: 'logistics' },
      { id: 'utilisation', label: 'Utilisation', icon: Gauge, clickable: true, useCase: 'logistics' },
      { id: 'fleet-timeline', label: 'Cost Manage', icon: Calendar, clickable: true, useCase: 'logistics' },
      { id: 'maintenance', label: 'Maintenance', icon: Wrench, clickable: true, useCase: 'logistics' },
      { id: 'carbon', label: 'Carbon', icon: Leaf, clickable: true, useCase: 'both' },
      { id: 'client-reporting', label: 'Scope III', icon: ChartNoAxesCombined, clickable: true, useCase: 'both' },
    ],
  },

  {
    // ===== OPTIMISATION =====
    id: 'optimization',
    label: 'Optimisation',
    icon: Lightbulb,
    clickable: true,
    useCase: 'logistics',
    children: [
      { id: 'electrification', label: 'Electrification', icon: Battery, clickable: true, useCase: 'logistics' },
      { id: 'ai-dispatch', label: 'AI Dispatch', icon: Sparkles, clickable: true, useCase: 'logistics' },
      { id: 'future-planning', label: 'Future Planning', icon: MapPinned, clickable: true, useCase: 'logistics' },
      { id: 'planning-demo', label: 'Planning Demo', icon: Sparkles, clickable: true, useCase: 'logistics' },
      { id: 'backloader', label: 'Backloader', icon: Package, clickable: true, useCase: 'logistics' },
      { id: 'collaborate', label: 'Collaborate', icon: Users, clickable: true, useCase: 'logistics' },
    ],
  },

  {
    // ===== TRUTH LAYER =====
    id: 'truth-layer',
    label: 'Truth Layer',
    icon: Layers,
    clickable: true,
    useCase: 'logistics',
    children: [
      { id: 'investigator', label: 'Investigator', icon: Crosshair, clickable: true, useCase: 'logistics' },
      { id: 'vehicle-scene', label: 'Vehicle Scene', icon: Eye, clickable: true, useCase: 'logistics' },
      { id: 'maintenance-truth', label: 'Maintenance', icon: Wrench, clickable: true, useCase: 'logistics' },
      { id: 'pricing-intelligence', label: 'Pricing Intel', icon: TrendingUp, clickable: true, useCase: 'logistics' },
      { id: 'zoe-question-log', label: 'Zoe Q&A Log', icon: MessagesSquare, clickable: true, useCase: 'logistics' },
      { id: 'truth-compute', label: 'Compute', icon: Cpu, clickable: true, useCase: 'logistics' },
      { id: 'data-import', label: 'Data Import', icon: Upload, clickable: true, useCase: 'logistics' },
    ],
  },
];

// Emergency Services-specific tabs
const emergencyTabs: AppTab[] = [
  {
    id: 'fleet-group',
    label: 'Fleet Report',
    icon: ChartNoAxesCombined,
    clickable: true,
    useCase: 'emergency',
    children: [
      { id: 'utilisation', label: 'Utilisation', icon: Gauge, clickable: true, useCase: 'emergency' },
      { id: 'response', label: 'Incidents', icon: Activity, clickable: true, useCase: 'emergency' },
      { id: 'fleet-timeline', label: 'Cost Manage', icon: Calendar, clickable: true, useCase: 'emergency' },
      { id: 'maintenance', label: 'Maintenance', icon: Wrench, clickable: true, useCase: 'emergency' },
      { id: 'carbon', label: 'Carbon', icon: Leaf, clickable: true, useCase: 'emergency' },
      { id: 'client-reporting', label: 'Scope III', icon: ChartNoAxesCombined, clickable: true, useCase: 'emergency' },
    ],
  },
  {
    id: 'optimization',
    label: 'Optimisation',
    icon: Lightbulb,
    clickable: true,
    useCase: 'emergency',
    children: [
      { id: 'ai-dispatch', label: 'AI Dispatch', icon: Sparkles, clickable: true, useCase: 'emergency' },
      { id: 'electrification', label: 'Electrification', icon: Battery, clickable: true, useCase: 'emergency' },
    ],
  },
  /** Control Centre (real-time hub); tab id kept as `dispatch` for existing permission keys. */
  { id: 'dispatch', label: 'Control Centre', icon: Database, clickable: true, useCase: 'emergency' },
];

// New truth-layer views can inherit the parent permission so existing users keep access.
const PAGE_PERMISSION_ALIASES: Record<string, string[]> = {
  investigator: ['investigator', 'truth-layer'],
  // Compute / pipelines: developer tooling under Truth Layer.
  'truth-compute': ['truth-compute', 'truth-layer'],
  // Data Import moved under Truth Layer; keep prior Control Centre / truth access paths.
  'data-import': ['data-import', 'truth-layer', 'fleet-data-hub'],
  // Legacy Cognito page id used US spelling; sidebar tab id is UK spelling.
  utilisation: ['utilisation', 'utilization'],
  // Profit Agent is its own permission — do not inherit Fleet Report → Profit.
  'profit-agent': ['profit-agent'],
  // New Profit tab: Cognito key `ops-profit` only (not legacy Profit / Control Centre).
  'ops-profit': ['ops-profit'],
  // System Health was a block on Control Centre: users who could open Control Centre keep access
  // until `system-health` is granted in Cognito (same temporary-alias pattern as planning-demo).
  'system-health': ['system-health', 'fleet-data-hub'],
  // Logistics AI Dispatch: same capability gate as Electrification under Optimisation until a dedicated permission exists.
  'ai-dispatch': ['ai-dispatch', 'electrification'],
  // Planning Demo under Optimisation. Prefer Cognito key `planning-demo`; Profit alias is temporary for existing users.
  'planning-demo': ['planning-demo', 'profitability-report'],
  // ZOE group: allow the section when any child agent is granted (parent id often omitted in Cognito).
  // Not `zoe-briefs`: Briefs is open to everyone (OPEN_PAGES), and must not open the Zoe chat with it.
  agents: ['agents', 'customs-agent', 'profit-agent'],
  // Assets / Configuration: keep cross-aliases so existing Cognito grants still open both.
  assets: ['assets', 'configuration'],
  configuration: ['configuration', 'assets'],
};


interface UserInfo {
  id: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  useCases: string[];
}

function App() {
  const { useCase, setUseCase } = useUseCase();

  // Prefer Control Centre as the default landing tab for Logistics
  const initialDefaultTab = useCase === 'logistics' ? 'fleet-data-hub' : 'fleet-group';
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const fromHash = typeof window !== 'undefined' ? getTabFromLocationHash() : null;
      if (fromHash) return fromHash;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('zecure_active_tab') : null;
      return stored || initialDefaultTab;
    } catch {
      return initialDefaultTab;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(() => new Set());

  // Check authentication on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { hasStoredSession, refreshSession } = await import('./auth/cognitoAuth');
        const { getDevUser } = await import('./auth/mockCognito');
        
        // Local dev without Cognito: run as the fixed dev user — the server
        // does the same (server/middleware/cognitoAuth.js).
        const devUser = getDevUser?.();
        if (devUser) {
          setIsAuthenticated(true);
          setUserInfo(devUser);
          return;
        }
        
        if (hasStoredSession()) {
          const tokens = await refreshSession();
          
          if (tokens) {
            // Extract user info from ID token
            const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
            
            const permissions = payload['custom:permissions'] 
              ? payload['custom:permissions'].split(',').map((p: string) => p.trim()).filter(Boolean)
              : [];
            
            const useCases = payload['custom:useCases']
              ? payload['custom:useCases'].split(',').map((u: string) => u.trim()).filter(Boolean)
              : ['logistics'];
            
            const userInfo: UserInfo = {
              id: payload.sub,
              email: payload.email,
              isAdmin: payload['custom:isAdmin'] === 'true',
              permissions,
              useCases
            };
            
            setIsAuthenticated(true);
            setUserInfo(userInfo);
          }
        }
      } catch (error) {
        console.error('Session restoration failed:', error);
        // Clear any stale data
        localStorage.removeItem('zecure_cognito_refresh_token');
      } finally {
        setIsLoading(false);
      }
    };
    
    restoreSession();
  }, []);

  // Lock use case based on user's assigned use cases
  useEffect(() => {
    if (userInfo && !userInfo.isAdmin) {
      const userUseCases = userInfo.useCases || [];
      // If user only has one use case, lock it to that use case
      if (userUseCases.length === 1) {
        const allowedUseCase = userUseCases[0] as 'logistics' | 'emergency';
        if (useCase !== allowedUseCase) {
          setUseCase(allowedUseCase);
        }
      }
      // If user doesn't have access to current use case, switch to their first available use case
      else if (userUseCases.length > 0 && !userUseCases.includes(useCase)) {
        const firstUseCase = userUseCases[0] as 'logistics' | 'emergency';
        setUseCase(firstUseCase);
      }
    }
  }, [userInfo, useCase, setUseCase]);

  // Handle login
  const handleLogin = (user: UserInfo) => {
    // Drop the "#login" entry hash (as the old landing shell did) once signed in.
    try {
      if (window.location.hash === '#login') {
        window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
      }
    } catch {
      // ignore history errors
    }
    setIsAuthenticated(true);
    setUserInfo(user);
  };

  // Handle logout
  const handleLogout = async () => {
    const { signOut } = await import('./auth/cognitoAuth');
    const { getDevUser } = await import('./auth/mockCognito');
    
    // No-auth local dev: there is no Cognito session to end — stay signed in
    // as the dev user (signOut() would throw with no pool configured).
    if (getDevUser?.()) return;
    
    signOut();
    // In-memory copies of the previous user's data (no page reload on sign-out).
    const [{ clearAssetsRosterSessionCopy }, { clearFleetCostAssetClassLabelsCache }] = await Promise.all([
      import('./utils/assetsRosterSessionCopy'),
      import('./utils/fleetCostAssetLabels'),
    ]);
    clearAssetsRosterSessionCopy();
    clearFleetCostAssetClassLabelsCache();
    
    setUserInfo(null);
    const defaultTab = useCase === 'logistics' ? 'fleet-data-hub' : 'fleet-group';
    // Reset tab state without writing #tab= to the URL.
    handleSetActiveTab(defaultTab, { updateHash: false });
    // Same entry URL as marketing Login: show Cognito login, not the old in-app landing shell.
    try {
      window.history.replaceState({}, '', '/login#login');
    } catch {
      // ignore history errors
    }
    setIsAuthenticated(false);
  };

  // Kept up to date every render so the session-expired subscription below
  // (which only subscribes once) always calls the latest handleLogout.
  const handleLogoutRef = useRef(handleLogout);
  handleLogoutRef.current = handleLogout;

  // Any fetch wrapper can report "the session is gone" (401 that survived a
  // refresh, or refreshSession() itself failing) via emitSessionExpired().
  // This is the single place that reacts by logging the user out and
  // dropping them back on the login screen.
  useEffect(() => {
    return onSessionExpired(() => {
      console.warn('[App] Session expired — redirecting to login');
      handleLogoutRef.current();
    });
  }, []);

  // Proactively renew the session on a timer while the app is open, instead
  // of only refreshing reactively when the user happens to trigger an API
  // call. Keeps an actively-used tab logged in without waiting for a request
  // to fail first.
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      try {
        const { refreshSession } = await import('./auth/cognitoAuth');
        const { getDevUser } = await import('./auth/mockCognito');
        // No Cognito session to renew in no-auth local dev.
        if (getDevUser?.()) return;
        const tokens = await refreshSession();
        if (!tokens) {
          emitSessionExpired();
        }
      } catch (error) {
        console.error('[App] Background session refresh failed:', error);
        emitSessionExpired();
      }
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  /** One vertical scrollport (main pane) — prevent document + main double scrollbars. */
  useEffect(() => {
    const root = document.documentElement;
    if (isAuthenticated) {
      root.classList.add('app-shell');
    } else {
      root.classList.remove('app-shell');
    }
    return () => root.classList.remove('app-shell');
  }, [isAuthenticated]);

  const computeDefaultTab = (tabIds: string[]) => {
    if (tabIds.includes('fleet-data-hub')) return 'fleet-data-hub';
    if (tabIds.includes('fleet-group')) return 'fleet-group';
    // Land on a page the user was granted before one that is open to everyone (Briefs).
    return tabIds.find((id) => !OPEN_PAGES.has(id)) ?? tabIds[0];
  };

  // Check if user has permission to access a page
  const hasPermission = (pageId: string): boolean => {
    if (!userInfo) return true; // Allow access before authentication (will be handled by Login)
    if (userInfo.isAdmin) return true; // Admins have access to everything
    if (OPEN_PAGES.has(pageId)) return true;
    const acceptedPermissionIds = PAGE_PERMISSION_ALIASES[pageId] || [pageId];
    return acceptedPermissionIds.some(permissionId => userInfo.permissions.includes(permissionId));
  };

  // Check if user has access to current use case
  const hasUseCaseAccess = (tabUseCase: 'logistics' | 'emergency' | 'both'): boolean => {
    if (!userInfo) return true; // Allow access before authentication (will be handled by Login)
    if (userInfo.isAdmin) return true; // Admins have access to everything
    
    // Check if user has access to the required use case
    if (tabUseCase === 'both') {
      // For 'both', user needs at least one use case
      return userInfo.useCases && userInfo.useCases.length > 0;
    }
    
    return userInfo.useCases?.includes(tabUseCase) || false;
  };

  // Combine tabs based on user's use cases (not just current useCase context)
  // This ensures users only see tabs for use cases they have access to
  const getAllowedTabs = () => {
    if (!userInfo || userInfo.isAdmin) {
      // Admins or no user info: show tabs based on current useCase context
      return useCase === 'logistics' ? logisticsTabs : emergencyTabs;
    }
    
    const userUseCases = userInfo.useCases || [];
    const allowedTabs: AppTab[] = [];
    
    // Include logistics tabs if user has logistics access
    if (userUseCases.includes('logistics')) {
      allowedTabs.push(...logisticsTabs);
    }
    
    // Include emergency tabs if user has emergency access
    if (userUseCases.includes('emergency')) {
      allowedTabs.push(...emergencyTabs);
    }
    
    // If user has no use cases, default to logistics (shouldn't happen, but safety check)
    if (allowedTabs.length === 0) {
      return logisticsTabs;
    }
    
    return allowedTabs;
  };

  const allMainTabs: AppTab[] = [
    ...sharedTabs,
    ...getAllowedTabs(),
  ];

  const filterByAccess = (tabs: AppTab[]): AppTab[] =>
    filterTabsByAccess(tabs, (tab) => hasPermission(tab.id) && hasUseCaseAccess(tab.useCase || 'both'));

  // Filter tabs based on user permissions and use case access (recursive for children)
  const mainTabs: AppTab[] = filterByAccess(allMainTabs);

  // Bottom tabs - Settings only visible to admins (internal use)
  const bottomTabs = userInfo?.isAdmin 
    ? [{ id: 'settings', label: 'Settings', icon: SettingsIcon, clickable: true, useCase: 'both' as const }]
    : [];

  // The global ZOE pop-up follows the same access rule as the ZOE sidebar
  // entry: shown only when that entry survived the permission/use-case filter.
  // Wait for userInfo: until it loads, hasPermission() allows every page.
  const findTab = (tabs: AppTab[], id: string): AppTab | undefined => {
    for (const t of tabs) {
      if (t.id === id) return t;
      const child = t.children ? findTab(t.children, id) : undefined;
      if (child) return child;
    }
    return undefined;
  };
  const showZoeLauncher = userInfo != null && findTab(mainTabs, 'agents') !== undefined;
  const activeTabLabel = findTab([...mainTabs, ...bottomTabs], activeTab)?.label;

  // Reset to fleet-group if current tab is not available
  useEffect(() => {
    const flattenTabIds = (tabs: AppTab[]): string[] => {
      const ids: string[] = [];
      for (const t of tabs) {
        ids.push(t.id);
        if (t.children) ids.push(...flattenTabIds(t.children));
      }
      return ids;
    };
    const allTabIds = [...flattenTabIds(mainTabs), ...bottomTabs.map(t => t.id)];
    if (allTabIds.length === 0) {
      // If no tabs are available, wait for userInfo to load
      return;
    }
    if (!allTabIds.includes(activeTab)) {
      // Set to Control Centre if available, otherwise fall back
      const defaultTab = computeDefaultTab(allTabIds);
      setActiveTab(defaultTab);
      try {
        localStorage.setItem('zecure_active_tab', defaultTab);
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCase, userInfo, mainTabs.length, bottomTabs.length, activeTab]);

  const handleSetActiveTab = (tabId: string, options?: { updateHash?: boolean }) => {
    if (tabId !== activeTab) {
      const warning = getUnsavedChangesWarning();
      if (warning && !window.confirm(warning)) {
        return;
      }
    }
    const updateHash = options?.updateHash !== false;
    setActiveTab(tabId);
    setMountedTabs(prev => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
    if (updateHash) {
      setTabLocationHash(tabId, 'push');
    }
    try {
      localStorage.setItem('zecure_active_tab', tabId);
    } catch {
      // ignore storage errors
    }

    // Lightweight "last used" tracking for Agents landing page.
    try {
      if (tabId === 'investigator') localStorage.setItem('agents_last_used_investigator', new Date().toISOString());
      if (tabId === 'customs-agent') localStorage.setItem('agents_last_used_customs', new Date().toISOString());
      if (tabId === 'profit-agent') localStorage.setItem('agents_last_used_profit', new Date().toISOString());
    } catch {
      // ignore
    }
  };

  // Listen for in-app navigation (e.g. Backloader → Posted Jobs)
  useEffect(() => {
    const handler = (e: CustomEvent<string>) => {
      if (e.detail && typeof e.detail === 'string') {
        handleSetActiveTab(e.detail);
      }
    };
    window.addEventListener('navigate-tab', handler as EventListener);
    return () => window.removeEventListener('navigate-tab', handler as EventListener);
  }, []);

  // Ensure the current active tab is always marked as mounted (first load)
  useEffect(() => {
    setMountedTabs(prev => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  // Logged-out / loading: don't show internal #tab= in the URL (landing + login look cleaner).
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    clearTabLocationHash();
  }, [isLoading, isAuthenticated]);

  // Logged in: keep hash in sync with active tab (replace avoids extra history noise on session restore).
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    const fromHash = getTabFromLocationHash();
    if (fromHash !== activeTab) {
      setTabLocationHash(activeTab, 'replace');
    }
  }, [isLoading, isAuthenticated, activeTab]);

  // Browser Back/Forward should navigate between tabs (popstate)
  useEffect(() => {
    const onPopState = () => {
      const tabFromHash = getTabFromLocationHash();
      if (tabFromHash && tabFromHash !== activeTab) {
        setActiveTab(tabFromHash);
        setMountedTabs(prev => {
          if (prev.has(tabFromHash)) return prev;
          const next = new Set(prev);
          next.add(tabFromHash);
          return next;
        });
        try {
          localStorage.setItem('zecure_active_tab', tabFromHash);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeTab]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Render tabs that have been visited at least once.
  // Only the active tab is visible; others stay mounted in the background
  // so they don't refetch every time you navigate back to them.
  const renderAllTabs = () => {
    const renderTab = (tabId: string) => {
      switch (tabId) {
      case 'fleet-group':
        return (
          <ErrorBoundary>
            <FleetReport />
          </ErrorBoundary>
        );
      case 'profitability-report':
        return (
          <ErrorBoundary>
            <ProfitabilityReport />
          </ErrorBoundary>
        );
      case 'utilisation':
        return (
          <ErrorBoundary>
            <FleetReport variant="utilisation-only" />
          </ErrorBoundary>
        );
      case 'carbon':
        return (
          <ErrorBoundary>
            <CarbonReporting />
          </ErrorBoundary>
        );
      case 'client-reporting':
        return (
          <ErrorBoundary>
            <ClientReporting />
          </ErrorBoundary>
        );
      case 'optimization':
        return (
          <ErrorBoundary>
            <Optimization />
          </ErrorBoundary>
        );
      case 'electrification':
        return (
          <ErrorBoundary>
            <Electrification />
          </ErrorBoundary>
        );
      case 'fleet-data-hub':
      case 'dispatch':
        return (
          <ErrorBoundary>
            <FleetDataHub />
          </ErrorBoundary>
        );
      case 'system-health':
        return (
          <ErrorBoundary>
            <SystemHealth />
          </ErrorBoundary>
        );
      case 'ops-profit':
        return (
          <ErrorBoundary>
            <OpsProfit />
          </ErrorBoundary>
        );
      case 'ai-dispatch':
        return (
          <ErrorBoundary>
            <AIDispatch />
          </ErrorBoundary>
        );
      case 'api-sources':
        return (
          <ErrorBoundary>
            <ApiSources />
          </ErrorBoundary>
        );
      case 'customs-agent':
        return (
          <ErrorBoundary>
            <CustomsAgent />
          </ErrorBoundary>
        );
      case 'profit-agent':
        return (
          <ErrorBoundary>
            <ProfitAgent />
          </ErrorBoundary>
        );
      case 'agents':
        return (
          <ErrorBoundary>
            <Agents />
          </ErrorBoundary>
        );
      case 'api-playground':
        return (
          <ErrorBoundary>
            <ApiPlayground />
          </ErrorBoundary>
        );
      case 'configuration':
        return (
          <ErrorBoundary>
            <Configuration />
          </ErrorBoundary>
        );
      case 'assets':
        return (
          <ErrorBoundary>
            <Assets />
          </ErrorBoundary>
        );
      case 'future-planning':
        return (
          <ErrorBoundary>
            <FuturePlanning />
          </ErrorBoundary>
        );
      case 'collaborate':
        return (
          <ErrorBoundary>
            <Collaborate />
          </ErrorBoundary>
        );
      case 'backloader':
        return (
          <ErrorBoundary>
            <Backloader />
          </ErrorBoundary>
        );
      case 'fleet-timeline':
        return (
          <ErrorBoundary>
            <CostManage />
          </ErrorBoundary>
        );
      case 'maintenance':
        return (
          <ErrorBoundary>
            <Maintenance />
          </ErrorBoundary>
        );
      case 'response':
        return (
          <ErrorBoundary>
            <Incidents />
          </ErrorBoundary>
        );
      case 'settings':
        return (
          <ErrorBoundary>
            <Settings />
          </ErrorBoundary>
        );
      case 'data-feed':
        return (
          <ErrorBoundary>
            <DataFeed />
          </ErrorBoundary>
        );
      case 'truth-layer':
        return (
          <ErrorBoundary>
            <FleetIntelligence />
          </ErrorBoundary>
        );
      case 'vehicle-scene':
        return (
          <ErrorBoundary>
            <VehicleSceneTimeline />
          </ErrorBoundary>
        );
      case 'maintenance-truth':
        return (
          <ErrorBoundary>
            <MaintenanceTruth />
          </ErrorBoundary>
        );
      case 'zoe-briefs':
        return (
          <ErrorBoundary>
            <ZoeBriefs />
          </ErrorBoundary>
        );
      case 'zoe-question-log':
        return (
          <ErrorBoundary>
            <ZoeQuestionLog />
          </ErrorBoundary>
        );
      case 'truth-compute':
        return (
          <ErrorBoundary>
            <TruthCompute />
          </ErrorBoundary>
        );
      case 'data-import':
        return (
          <ErrorBoundary>
            <DataImport />
          </ErrorBoundary>
        );
      case 'investigator':
        return (
          <ErrorBoundary>
            <Investigator />
          </ErrorBoundary>
        );
      case 'pricing-intelligence':
        return (
          <ErrorBoundary>
            <PricingIntelligencePage />
          </ErrorBoundary>
        );
      case 'planning-demo':
        return (
          <ErrorBoundary>
            <PlanningDecisionPage />
          </ErrorBoundary>
        );
      default:
        return null;
      }
    };

    const tabsToRender = mountedTabs.size > 0 ? Array.from(mountedTabs) : [activeTab];

    return (
      <>
        {tabsToRender.map((tabId) => (
          <div
            key={tabId}
            className={activeTab === tabId ? 'is-active-tab' : undefined}
            style={{ display: activeTab === tabId ? 'block' : 'none' }}
          >
            {renderTab(tabId) ?? (
              <ErrorBoundary>
                <FleetDataHub />
              </ErrorBoundary>
            )}
          </div>
        ))}
      </>
    );
  };

  // Logged-out users go straight to the login screen (marketing lives at zeefleet.co.uk).
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <TabProvider activeTab={activeTab} setActiveTab={handleSetActiveTab}>
      <ZoeConversationProvider popupAvailable={showZoeLauncher}>
      {/* h-screen + min-h-0 so the main pane is the scrollport (needed for position:sticky). */}
      <div className="h-screen bg-background flex overflow-hidden">
        <div className={`fixed left-0 top-0 h-screen transition-width duration-500 ease-out ${sidebarCollapsed ? 'w-12' : 'w-64'}`}>
          <Sidebar 
            mainTabs={mainTabs} 
            bottomTabs={bottomTabs} 
            activeTab={activeTab} 
            onChange={handleSetActiveTab} 
            userInfo={userInfo}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(s => !s)}
            onLogout={handleLogout}
          />
        </div>

        <div className={`flex-1 flex flex-col min-h-0 min-w-0 transition-margin duration-500 ease-out ${sidebarCollapsed ? 'ml-12' : 'ml-64'}`}>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain bg-background min-w-0">
            {/* Extra bottom padding clears the floating ZOE button, so it never
                covers the last table row or a table's horizontal scrollbar. */}
            <div className={`pl-4 pr-8 pt-8 w-full min-w-0 ${showZoeLauncher ? 'pb-28' : 'pb-8'}`}>
              {renderAllTabs()}
            </div>

            <footer className="bg-card border-t border-border mt-16">
              <div className="px-8 py-6">
                <p className="text-sm text-muted-foreground text-center">
                  ZEEFLEET Dashboard - Powered by © BEAT 2026
                </p>
              </div>
            </footer>
          </div>
        </div>
      </div>
      {showZoeLauncher ? <ZoeLauncher pageLabel={activeTabLabel} /> : null}
      </ZoeConversationProvider>
    </TabProvider>
  );
}

export default App;
